import { STAGE_DISPLAY_NAMES, WORKFLOW_TYPE_DISPLAY_NAMES, WorkflowTaskStatus } from '../../../domain/types/workflow';

export function getStageName(stage: string): string {
  return STAGE_DISPLAY_NAMES[stage] || stage;
}

export function getWorkflowDisplayName(type: string): string {
  return WORKFLOW_TYPE_DISPLAY_NAMES[type] || type;
}

export function formatYear(periodOfReviewId: number): string {
  return `${periodOfReviewId - 1}-${periodOfReviewId}`;
}

/** Badge class for workflow task status (Active, Completed, Skipped). */
export function getTaskStatusClass(status: WorkflowTaskStatus | string): string {
  switch (status) {
    case WorkflowTaskStatus.Active:
      return 'badge-primary';
    case WorkflowTaskStatus.Completed:
    case 'Completed':
      return 'badge-success';
    case WorkflowTaskStatus.Cancelled:
    case 'Cancelled':
      return 'badge-secondary';
    case WorkflowTaskStatus.Skipped:
    case 'Skipped':
      return 'badge-secondary';
    default:
      return 'badge-light';
  }
}

/** Gate-aware display fields for a task summary. */
interface GatedTaskLike {
  status: WorkflowTaskStatus | string;
  gateStatus?: 'Upcoming' | 'Open' | 'PastDue' | null;
  pastDue?: boolean;
}

/**
 * Display status for a task, folding in the date gate so list/progress views
 * agree with the task-detail page: an open Active task
 * whose gate hasn't opened shows "Upcoming"; one past its due date shows
 * "Past Due". Completed/Cancelled/Skipped always show the stored status.
 */
export function getDisplayTaskStatus(task: GatedTaskLike): string {
  if (task.status === WorkflowTaskStatus.Active || task.status === 'Active') {
    if (task.gateStatus === 'Upcoming') return 'Upcoming';
    if (task.pastDue || task.gateStatus === 'PastDue') return 'Past Due';
  }
  return task.status;
}

/** Badge class matching getDisplayTaskStatus. */
export function getDisplayTaskStatusClass(task: GatedTaskLike): string {
  const display = getDisplayTaskStatus(task);
  if (display === 'Upcoming') return 'badge-secondary';
  if (display === 'Past Due') return 'badge-warning';
  return getTaskStatusClass(task.status);
}

/** Badge class for case-level status (Active, Complete, Cancelled). */
export function getCaseStatusClass(status: string): string {
  switch (status) {
    case 'Active': return 'badge-primary';
    case 'Complete': return 'badge-success';
    case 'Cancelled': return 'badge-danger';
    default: return 'badge-secondary';
  }
}

/** Extracts display-ready error message from HTTP error response with fallback chain. */
export function extractErrorMessage(err: any, defaultMsg: string): string {
  if (typeof err === 'string') return err;
  return (typeof err?.error?.error === 'string' ? err.error.error : undefined)
      ?? (typeof err?.error?.message === 'string' ? err.error.message : undefined)
      ?? (typeof err?.error === 'string' ? err.error : undefined)
      ?? (typeof err?.message === 'string' ? err.message : undefined)
      ?? defaultMsg;
}

/** Extracts date from ISO UTC string and returns a Date in local timezone, completely ignoring the timezone offset. */
export function extractDateFromUtcString(dateString: string | null | undefined): Date | null {
  if (!dateString) return null;
  try {
    const [year, month, day] = dateString.split('T')[0].split('-');
    // Create Date at midnight in local timezone
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  } catch {
    return null;
  }
}
